import { httpsCallable } from 'firebase/functions';
import { userService } from '../services/userService';
import { auth, functions } from '../firebase/config';

// NOTE: Terra API credentials (dev-id and x-api-key) are NOT in this file.
// All calls to api.tryterra.co are proxied through Firebase Cloud Functions:
//   - generateTerraWidgetSession
//   - getTerraUsersByReferenceId
//   - deauthenticateOwnTerraDevice
// See functions/src/terraConnect.ts and functions/src/terraCredentials.ts.

interface TerraDevice {
  id: string;
  resource: string;
  connectedAt: string;
  expired?: boolean;
}

export interface TerraAuthResponse {
  expires_in: number;
  session_id: string;
  status: string;
  url: string;
}

export const MAX_ACTIVE_DEVICES = 2;

/**
 * Generate a Terra widget session URL for the current user.
 *
 * The `redirectEndpoint` argument is kept for backwards compatibility but is
 * unused — the backend always uses `${origin}/connectionsuccess` and
 * `${origin}/connectionfailed` as the redirect URLs.
 *
 * Whether Apple Health is offered in the widget is decided server-side (see
 * generateTerraWidgetSession) from the caller's own device records — not a
 * client-supplied list, so it can't be bypassed or go stale here.
 */
export const generateTerraAuthUrl = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _redirectEndpoint: string = '/profile'
): Promise<TerraAuthResponse> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  console.log('Generating Terra auth URL for user:', user.uid);

  const callable = httpsCallable<
    { origin: string },
    TerraAuthResponse
  >(functions, 'generateTerraWidgetSession');

  try {
    const result = await callable({ origin: window.location.origin });
    console.log('Parsed Terra API Response:', result.data);
    return result.data;
  } catch (error) {
    console.error('Terra API Error:', error);
    throw error;
  }
};

export const fetchTerraUsersByReferenceId = async (): Promise<
  { user_id: string; provider: string; active: boolean }[]
> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const callable = httpsCallable<
    Record<string, never>,
    { users: { user_id: string; provider: string; active: boolean }[] }
  >(functions, 'getTerraUsersByReferenceId');

  const result = await callable({});
  return result.data.users || [];
};

export const saveTerraUserId = async (terraUserId: string, resource: string = 'UNKNOWN') => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  try {
    const userData = await userService.getUserData(user.uid);
    if (!userData) throw new Error('User not found');

    const currentDevices = (userData?.deviceIDs || []) as TerraDevice[];

    // Mark any existing device of the same resource type as expired
    const updatedDevices = currentDevices.map((device: TerraDevice) => {
      if (device.resource === resource && !device.expired) {
        return { ...device, expired: true };
      }
      return device;
    });

    await userService.updateUserProfile(user.uid, {
      deviceIDs: [
        ...updatedDevices,
        {
          id: terraUserId,
          resource: resource,
          connectedAt: new Date().toISOString(),
          expired: false
        }
      ]
    });

    console.log('Terra device ID saved successfully');
  } catch (error) {
    console.error('Error saving Terra device ID:', error);
    throw error;
  }
};

/**
 * Deauthenticate a Terra device and mark it as expired.
 *
 * The Cloud Function performs both the Terra API call and the Firestore
 * update (with a Firestore-only fallback if Terra's API fails), so the
 * frontend just needs to await the callable.
 *
 * @param terraUserId - The Terra user ID (device ID) to deauthenticate
 * @param _deviceIndex - kept for backwards compatibility, unused
 */
export const deauthenticateTerraDevice = async (
  terraUserId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _deviceIndex?: number,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  console.log('Deauthenticating Terra device:', terraUserId);

  const callable = httpsCallable<
    { deviceId: string },
    { success: boolean }
  >(functions, 'deauthenticateOwnTerraDevice');

  try {
    await callable({ deviceId: terraUserId });
    console.log('Terra device marked as expired successfully');
  } catch (error) {
    console.error('Error deauthenticating Terra device:', error);
    throw new Error(
      'Failed to disconnect device. Please check your connection and try again.',
    );
  }
};
